import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { ROUTES } from 'src/constants/route';
import { REDIRECT_DELAY_SECONDS } from 'src/constants/timing';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import ErrorIllustration from 'src/assets/su-error-illustration-2.png';
import { ERROR_ILLUSTRATION_ALT } from 'src/constants/entity';

// Test IDs for component testing
export const TEST_IDS = {
  NOT_FOUND_ILLUSTRATION: 'not-found-illustration',
  REDIRECT_LINK: 'redirect-link',
} as const;

/**
 * NotFoundPage component displays a error page with auto-redirect functionality.
 * Shows an error illustration, translated messages, and redirects to homepage after 5 seconds
 * or immediately when the redirect link is clicked.
 */
const NotFoundPage: React.FC = () => {
  const location = useLocation();
  const { viewMetric, clickMetric } = useMetrics();
  const [coreBundle] = useBundle('core');
  const [countdown, setCountdown] = React.useState(REDIRECT_DELAY_SECONDS);

  // Track page view on mount
  useEffect(() => {
    viewMetric({
      feature: FeatureName.NotFoundPage,
      metadata: { path: location.pathname },
    });
  }, [viewMetric, location.pathname]);

  // Countdown timer effect
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          window.location.href = ROUTES.LEARN.BASE;
          return prev; // Component will unmount, so this return value doesn't matter
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup interval on unmount
    return () => {
      if (typeof clearInterval !== 'undefined') {
        clearInterval(countdownInterval);
      }
    };
  }, []);

  return (
    <main
      className="flex min-h-screen w-full flex-col items-center px-su-page-padding pt-su-space-07"
      aria-labelledby="not-found-header"
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-su-space-05">
        {/* Image Frame */}
        <div className="flex flex-col items-center gap-su-space-02">
          <img
            src={ErrorIllustration}
            className="h-auto w-full max-w-lg"
            alt={ERROR_ILLUSTRATION_ALT}
            data-testid={TEST_IDS.NOT_FOUND_ILLUSTRATION}
          />
        </div>

        {/* Main Text Group */}
        <div className="w-full text-center">
          <h1
            id="not-found-header"
            className="!text-su-heading-600 !font-bold text-su-text-primary"
          >
            {t(coreBundle, 'not_found_header')}
          </h1>
        </div>

        {/* Clickable Text Link with Countdown */}
        <div className="text-center">
          <a
            href={ROUTES.LEARN.BASE}
            onClick={() => {
              clickMetric({
                feature: FeatureName.RedirectHomeLink,
                metadata: { path: location.pathname },
              });
            }}
            className="cursor-pointer text-center text-su-heading-100 font-normal text-su-text-link hover:underline"
            data-testid={TEST_IDS.REDIRECT_LINK}
            aria-live="polite"
            aria-atomic="true"
          >
            {t(coreBundle, 'redirect_message')}
            {' '}
            {countdown}
          </a>
        </div>
      </div>
    </main>
  );
};

export default NotFoundPage;
